/**
 * @lines98/client/lib/profile-name-edit — чистый предикат «можно ли сохранить имя»
 * (П10, тикет `docs/orchestrator/tickets/fe-auto-2026-06-22-bundle.md` AC-10.1..10.4).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §3 «логика → lib/», §4.1 single-source;
 * owner-bug 2026-06-22 П10): критерий «кнопка „Сохранить“ активна» — это ПРАВИЛО
 * (trim; ≠ текущему; непустое; валидная длина 2..24 кодпойнта; first-set — нет «текущего»
 * для сравнения). Раньше оно было зашито inline в `buildEdit` (profile-screen.ts) и
 * НЕ тестировалось в node-env. Вынос в чистый predicate даёт ОДИН источник истины:
 *   (1) input-listener реактивно дизейблит «Сохранить» из этого же правила;
 *   (2) Enter-guard и клик зовут тот же предикат (нет дрейфа между путями сабмита);
 *   (3) правило юнит-тестируемо без браузера.
 *
 * Длина — В КОДПОЙНТАХ (как `codepointLength` в profile-screen.ts: эмодзи/суррогаты).
 * Лимиты длины — ЕДИНЫЙ источник здесь (NAME_MIN/NAME_MAX), profile-screen импортирует
 * их отсюда (no-hardcode, не два расходящихся набора чисел).
 *
 * SCOPE: чистая функция `canSaveName` + константы длины. NOT IN SCOPE: сетевая мутация
 * имени (profile-screen `onSaveName`), стоимость смены (NICKNAME_CHANGE_COST — config).
 *
 * Cross-references:
 *   - screens/profile-screen.ts (`buildEdit` input-listener + Enter-guard — consumer).
 *   - server change-display-name.ts (зеркало серверной длины 2..24 кодпойнта).
 */

/** Минимум длины имени в кодпойнтах (зеркало серверной DISPLAY_NAME-политики). */
export const NAME_MIN_CODEPOINTS = 2;
/** Максимум длины имени в кодпойнтах (зеркало серверной DISPLAY_NAME-политики). */
export const NAME_MAX_CODEPOINTS = 24;

/** Длина строки в КОДПОЙНТАХ (а не UTF-16 units) — корректно для эмодзи/суррогатов. */
function codepointLength(s: string): number {
  return [...s].length;
}

/** Аргументы предиката «можно ли сохранить имя». */
export interface CanSaveNameArgs {
  /** Сырое значение поля (helper сам делает trim). */
  readonly value: string;
  /** Текущий отображаемый ник (currentNick()); для first-set — '' или null. */
  readonly currentName: string | null;
  /** true если имя ещё не задано (первая установка, isUnsetNick()). */
  readonly isUnset: boolean;
}

/**
 * Можно ли сохранить введённое имя (П10 AC-10.1..10.4). Чистая функция.
 *
 * true  ⇔ trimmed непустое И валидная длина (2..NAME_MAX) И
 *         (isUnset ИЛИ trimmed ≠ currentName).
 * false ⇔ пусто / == текущему / короче минимума / длиннее максимума.
 *
 * first-set (isUnset): нет «текущего» для сравнения → критерий = «непустое + валидная
 * длина» (AC-10.3). Сравнение и длина — в кодпойнтах (AC-10.4).
 */
export function canSaveName({ value, currentName, isUnset }: CanSaveNameArgs): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false; // пусто → нельзя
  const len = codepointLength(trimmed);
  if (len < NAME_MIN_CODEPOINTS || len > NAME_MAX_CODEPOINTS) return false; // вне длины → нельзя
  if (isUnset) return true; // first-set: нет «текущего» — непустое+валидное достаточно
  return trimmed !== (currentName ?? '').trim(); // изменилось относительно текущего
}
